import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { listAgents } from "@kritaka/core";
import { useEffect, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { PlatformIcon } from "./platform-icons.js";
function agentIconPlatform(agent) {
    if (agent.platform === "win32")
        return "windows";
    if (agent.platform === "darwin")
        return "macos";
    if (agent.platform === "linux")
        return "linux";
    return "unknown";
}
function isEditor(agent) {
    const settings = typeof agent.settings === "string"
        ? safeJsonObject(agent.settings)
        : agent.settings ?? null;
    return Boolean(settings && settings.is_editor);
}
function safeJsonObject(raw) {
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    }
    catch {
        return null;
    }
}
export function AgentList({ workspaceId, selectedAgentId, onSelect, filter = "agents", agentPresence, }) {
    const ctx = useDataContext();
    const [agents, setAgents] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setAgents(null);
        setError(null);
        const load = () => {
            listAgents(ctx, workspaceId)
                .then((a) => {
                if (!cancelled)
                    setAgents(a);
            })
                .catch((e) => {
                if (!cancelled)
                    setError(e instanceof Error ? e.message : String(e));
            });
        };
        load();
        // Poll every 15s so connected-state freshness (30s window) stays current
        // without wiring a relay presence frame for every message. Cheap — the
        // response is small and cached by Hub.
        const timer = setInterval(load, 15_000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [ctx, workspaceId]);
    if (error)
        return _jsxs("div", { style: { color: "var(--error)" }, children: ["Hub error: ", error] });
    if (!agents)
        return _jsx("div", { style: { color: "var(--text-muted)" }, children: "Loading agents\u2026" });
    const visible = agents.filter((a) => (filter === "editors" ? isEditor(a) : !isEditor(a)));
    if (visible.length === 0)
        return (_jsx("div", { style: { color: "var(--text-muted)", fontSize: 12, padding: "4px 8px" }, children: filter === "editors" ? "No editors." : "No agents." }));
    return (_jsx("ul", { style: { listStyle: "none", padding: 0, margin: 0 }, children: visible.map((a) => {
            const selected = a.id === selectedAgentId;
            const platform = agentIconPlatform(a);
            const live = agentPresence?.get(a.id);
            const statusLabel = live === "active" ? "active" : live === "idle" ? "idle" : a.status;
            const liveState = live ?? (a.status === "running" ? "active" : "offline");
            // KTK-274 — platform icon color follows the row's state. Selected rows
            // adopt the accent ink so the platform icon doesn't look out of place
            // against the accent-tinted row background.
            const stateColor = selected
                ? "var(--accent-ink)"
                : liveState === "active"
                    ? "var(--p4)"
                    : liveState === "idle"
                        ? "var(--ink)"
                        : "var(--ink-4)";
            const stateOpacity = !selected && liveState === "offline" ? 0.6 : 1;
            const titleParts = [statusLabel, a.model];
            if (a.last_seen_at)
                titleParts.push(`last seen ${a.last_seen_at}`);
            return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => onSelect(a), style: {
                        width: "100%",
                        textAlign: "left",
                        padding: "4px 8px 4px 22px",
                        background: selected
                            ? "color-mix(in oklab, var(--accent-soft) 60%, var(--paper-2))"
                            : "transparent",
                        border: "none",
                        borderLeft: `2px solid ${selected ? "var(--accent)" : "transparent"}`,
                        borderRadius: 0,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 13,
                        color: selected ? "var(--ink)" : "var(--ink-2)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minHeight: 24,
                        transition: "background 80ms ease",
                    }, onMouseEnter: (e) => {
                        if (!selected)
                            e.currentTarget.style.background = "var(--paper-3)";
                    }, onMouseLeave: (e) => {
                        if (!selected)
                            e.currentTarget.style.background = "transparent";
                    }, children: [_jsx(PlatformIcon, { platform: platform, size: 14, title: titleParts.join(" • "), style: { color: stateColor, opacity: stateOpacity, flexShrink: 0 } }), _jsx("span", { style: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }, children: a.name })] }) }, a.id));
        }) }));
}
//# sourceMappingURL=AgentList.js.map